import React, { useState, useEffect, useCallback } from "react";
import "../../styles/Produits.css";
import ServiceSection from "./ServiceSectionProducts";
import defaultImage from "../../assets/im&1.png";
import hab2 from "../../assets/rouge.png";
import { useOutletContext } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import "../../../i18n";
import { useNavigate } from "react-router-dom";
import { useCart } from "../CartContext";


const Produits = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
      const { cart, setCart, cartCount, setCartCount } = useCart();
  
  const [categories, setCategories] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [translatedCategories, setTranslatedCategories] = useState({});
  const [translatedProducts, setTranslatedProducts] = useState({});
  const lang = localStorage.getItem('language') || 'FRA';
  const newlang = lang === "FRA" || lang === "FR" || lang === ""? 'FR' : lang === "ENG" ? 'EN' : lang === "ESP" ? 'ES' : 'PT';

  const { incrementCartCount, addToCart} = useOutletContext();

  // Utilisation d'une fonction mémorisée pour traduire les textes
  const translateText = useCallback(async (text, targetLanguage) => {
    if (targetLanguage === 'FR') return text; // No need to translate if the language is FR
    const apiKey = process.env.REACT_APP_DEEPL_API_KEY;
    const url = process.env.REACT_APP_DEEPL_URL;
    const params = new URLSearchParams();
    params.append("auth_key", apiKey);
    params.append("text", text);
    params.append("target_lang", targetLanguage);

    try {
      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
        },
        body: params,
      });
      const data = await response.json();
      return data.translations?.[0]?.text || text;
    } catch (error) {
      console.error("Erreur de traduction", error);
      return text;
    }
  }, []);

  // Charge les catégories et traduit leurs noms
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await fetch(process.env.REACT_APP_BASE_URL + '/api/shop/products', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'X-API-Key': process.env.REACT_APP_API_KEY,
          },
        });
        if (!response.ok) {
          if (response.status === 500) {
            // Redirection vers la page 500
            navigate("/500");
          }
          throw new Error(`Erreur HTTP : ${response.status}`);
        }
        const data = await response.json();
        const translatedCategoryNames = {};

        for (const [categoryName, categoryData] of Object.entries(data)) {
          const translatedName = await translateText(categoryData.name, newlang);
          translatedCategoryNames[categoryName] = { ...categoryData, name: translatedName };
        }

        setCategories(data);
        setTranslatedCategories(translatedCategoryNames);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCategories();
  }, [newlang, translateText]);

  // Traduction des produits à la volée
  useEffect(() => {
    const translateProducts = async () => {
      const translated = {};

      for (const [categoryKey, category] of Object.entries(categories)) {
        const translatedCategoryProducts = await Promise.all(
          category.products.map(async (product) => {
            const translatedName = await translateText(product.name, newlang);
            return { ...product, name: translatedName };
          })
        );

        translated[categoryKey] = { ...category, products: translatedCategoryProducts };
      }

      setTranslatedProducts(translated);
    };

    if (Object.keys(categories).length > 0) {
      translateProducts();
    }
  }, [categories, newlang, translateText]);

  const [activeContent, setActiveContent] = useState("tout");

  const handleNavigation = (content) => {
    setActiveContent(content);
  };

  const isActive = (content) => activeContent === content;

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const allProducts = Object.values(translatedProducts).flatMap((category) => category.products);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentProducts = allProducts.slice(indexOfFirstItem, indexOfLastItem);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="ticket-container">
      <div className="ticketext"> {t("ticket_title")}</div>

      <header className="ticket-header">
        <nav>
          <ul>
            <li
              className={isActive("tout") ? "active" : ""}
              onClick={() => handleNavigation("tout")}>
              {t('nav_items.tout')}
            </li>
            {Object.entries(translatedCategories).map(([categoryName, categoryData]) => (
              <li
                key={categoryData.id}
                className={isActive(categoryName) ? "active" : ""}
                onClick={() => handleNavigation(categoryName)}>
                {categoryData.name.toUpperCase()}
              </li>
            ))}
          </ul>
        </nav>
      </header>

      <div className="non">
        {activeContent === "tout" ? (
          <div>
            <ToastContainer position="top-right" autoClose={800} />
            <div className="category-section">
              <div className="products-container">
                {currentProducts.map((product) => (
                  <div className="product-card" key={product.id}>
                    <img
                      src={`${process.env.REACT_APP_BASE_URL}/${product.image}`}
                      alt={product.name}
                      className="product-image"
                      loading="lazy" // Lazy loading des images
                    />
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-price">{product.price.toLocaleString()} FCFA</p>
                    <button
                      className="add-to-cart-button"
                      onClick={() =>
                        addToCart(
                          "product-" + product.id,
                          product.name,
                          product.price,
                          product.type,
                          product.image,
                          product.description
                        )
                      }>
                      <i className="fa fa-shopping-cart"></i>  {t('categories.add_to_cart')}
                    </button>
                  </div>
                ))}
              </div>
              <div className="pagination">
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}>
                  &lt;
                </button>
                {[...Array(Math.ceil(allProducts.length / itemsPerPage)).keys()].map((page) => (
                  <button
                    key={page + 1}
                    className={`pagination-button ${currentPage === page + 1 ? 'active' : ''}`}
                    onClick={() => handlePageChange(page + 1)}>
                    {page + 1}
                  </button>
                ))}
                <button
                  className="pagination-button"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === Math.ceil(allProducts.length / itemsPerPage)}>
                  &gt;
                </button>
              </div>
            </div>
          </div>
        ) : (
          <div>
            <ToastContainer position="top-right" autoClose={800} />
            {categories[activeContent] && (
              <div className="category-section">
                <div className="products-container">
                  {translatedProducts[activeContent]?.products.map((product) => (
                    <div className="product-card" key={product.id}>
                      <img
                        src={`${process.env.REACT_APP_BASE_URL}/${product.image}`}
                        alt={product.name}
                        className="product-image"
                        loading="lazy" // Lazy loading des images
                      />
                      <h3 className="product-name">{product.name}</h3>
                      <p className="product-price">{product.price.toLocaleString()} FCFA</p>
                      <button
                        className="add-to-cart-button"
                        onClick={() =>
                          addToCart(
                            "product-" + product.id,
                            product.name,
                            product.price,
                            product.type,
                            product.image,
                            product.description
                          )
                        }>
                        <i className="fa fa-shopping-cart"></i>  {t('categories.add_to_cart')}
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div>
        <ServiceSection />
      </div>
    </div>
  );
};

export default Produits;
